// The task drawer (docs/plans/2026-10-02-borden-ontwerp.md §12.5), shared by
// the project's board and its tasks page: details, report, workflow progress
// and history, decisions, plus the board-era additions: ticket key, stream and
// lane, severity, links, custom vars, "Move to stream…" and Start with the WIP
// confirmation. The drawer fetches its own task; TaskCreateDrawer creates one.
//
// A task's workflow is started by the flow engine, which resolves the agent
// from the project's role/workflow policy (FD §16.3), so there is no "assign".

import { LiveDuration } from '../live-duration'
import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import type {
  CustomVars,
  Project,
  Role,
  Severity,
  StreamDefinition,
  Task,
  WipLimitReachedDetails,
  WorkflowDefinition,
} from '@goblic/shared'
import { RiskLevel, SEVERITIES, TaskStatus, WIP_LIMIT_REACHED_CODE } from '@goblic/shared'
import {
  ApiError,
  apiErrorDetails,
  approvalsApi,
  streamsApi,
  tasksApi,
  workflowsApi,
} from '../../lib/api-client'
import { StatusBadge } from '../status-badge'
import { StepChain } from '../step-chain'
import { DecisionCard, ModelTierBadge, WorkflowVariablesPanel, findDecisionState } from './decision'
import { CustomVarsEditor, CustomVarsSection } from './custom-vars'
import { formRowsToVars, type VarRow } from './custom-vars-rows'
import { TaskLinks } from './task-links'
import { SubtaskParentLine, SubtasksPanel } from './subtasks'
import { Drawer, ErrorNote, Field, LoadingRows, Notice, Tabs } from '../ui'
import { useWorkspaceId } from '../../lib/auth'
import { useDialogs, type DialogApi } from '../dialogs'
import { useLookups } from '../../hooks/useLookups'
import { formatMoney, formatRelative, humanize } from '../../lib/format'
import { ClayIcon, ClayList } from '../goblin-art'
import { TaskReport } from '../reports/task-report'
import { useTaskReport } from '../../hooks/useReports'
import { QUEUE_LANE_LABEL, SEVERITY_CLASS } from '../board/board-utils'

export type TaskDrawerTab = 'details' | 'report'

// A blocked workflow task is started again to retry its current state.
export const STARTABLE: readonly TaskStatus[] = [
  TaskStatus.Pending,
  TaskStatus.Validating,
  TaskStatus.Blocked,
]

/**
 * Whether the "Start run" / "Retry" action applies. A workflow task that ended
 * in a failure terminal state (e.g. "Blocked" after max_rounds) is also
 * `blocked`, but has no state left to retry; `workflows` (the project's) tells
 * those apart.
 */
export function isStartable(task: Task, workflows: WorkflowDefinition[] | undefined): boolean {
  if (!STARTABLE.includes(task.status)) return false
  if (task.status !== TaskStatus.Blocked || !task.workflowKey) return true
  const state = workflows
    ?.find((w) => w.key === task.workflowKey)
    ?.states.find((st) => st.name === task.workflowState)
  return state?.kind !== 'terminal'
}

function refLabel(ref: { system: string; externalId: string; refType?: string | null }): string {
  if (ref.refType === 'pull_request') return `Pull request #${ref.externalId}`
  if (ref.refType === 'branch') return `Branch ${ref.externalId}`
  if (ref.refType === 'commit') return `Commit ${ref.externalId.slice(0, 7)}`
  return `${humanize(ref.system)} ${ref.externalId ?? ''}`.trim()
}

/** How often an open task refreshes while something is happening to it. */
export const LIVE_REFRESH_MS = 4000

/** A task whose state can change on its own: a run is going or a person is being asked. */
export function isLive(task: Task): boolean {
  return (
    task.status === TaskStatus.Assigned ||
    task.status === TaskStatus.InProgress ||
    task.status === TaskStatus.AwaitingApproval
  )
}

export function isEditable(task: Task): boolean {
  // A human step (awaiting_approval, workflow task) has no running step, so its
  // description can be completed too (PATCH /tasks/:id, ontwerp §5.4).
  return (
    task.status === TaskStatus.Pending ||
    task.status === TaskStatus.Blocked ||
    (task.status === TaskStatus.AwaitingApproval && task.workflowKey != null)
  )
}

// Matches the message workflow-engine.service.ts's `startAgentStep` blocks a
// task with when config-resolution.service.ts's `assertRiskWithinCeiling`
// rejects the task's risk level against the state's role ceiling.
const RISK_MISMATCH_RE = /exceeds role's allowed risk level "(\w+)"/

function riskMismatchHint(reason: string | null | undefined): string | null {
  const match = reason ? RISK_MISMATCH_RE.exec(reason) : null
  return match
    ? `This step's role only takes up to ${match[1]}-risk tasks. Lower the task's risk or allow higher risk on the role.`
    : null
}

/** The reason the task's latest workflow transition blocked it, or null (not blocked, or no workflow). Shares its query with `TaskWorkflowSection`'s own transitions list, so this adds no extra request. */
function useBlockReason(task: Task | null): string | null {
  const enabled = task != null && task.status === TaskStatus.Blocked && task.workflowKey != null
  const query = useQuery({
    queryKey: ['task', task?.id ?? '', 'transitions'],
    queryFn: () => tasksApi.transitions(task!.id),
    enabled,
  })
  if (!enabled) return null
  const transitions = query.data ?? []
  return transitions[transitions.length - 1]?.reason ?? null
}

export function useRiskMismatchHint(task: Task | null): string | null {
  return riskMismatchHint(useBlockReason(task))
}

export const SOURCE_LABEL: Record<string, string> = {
  manual: 'Added by hand',
  plane_webhook: 'Plane',
  github_webhook: 'GitHub',
  schedule_or_cron: 'Schedule',
  api_call: 'API',
  run_follow_up: 'Follow-up',
}

/** One `PreconditionCheckResult` check (packages/api/src/services/domain-types.ts), as it rides along on a 422's `ApiError.details`. */
interface PreconditionCheck {
  id: string
  passed: boolean
  reason?: string | null
}

/** Extracts the failed-precondition list from a `POST /tasks/:id/submit` 422 (routes/tasks/index.ts), if that's what this error is. */
function preconditionFailures(error: unknown): PreconditionCheck[] | null {
  if (!(error instanceof ApiError) || error.status !== 422) return null
  const details = error.details as
    | { error?: { details?: { result?: { checks?: PreconditionCheck[] } } } }
    | undefined
  const checks = details?.error?.details?.result?.checks
  return checks ? checks.filter((c) => !c.passed) : null
}

export function startError(error: unknown): ReactNode {
  const failed = preconditionFailures(error)
  if (failed && failed.length > 0) {
    return (
      <div>
        <p>This task can’t start yet:</p>
        <ul className="mt-1 list-disc pl-5">
          {failed.map((check) => (
            <li key={check.id}>{check.reason ?? humanize(check.id)}</li>
          ))}
        </ul>
      </div>
    )
  }
  if (error instanceof ApiError && error.status === 422)
    return `This task can’t start yet: ${error.message}`
  return error instanceof Error ? error.message : 'Couldn’t start the run.'
}

/** Starts a task; when its stream is at its WIP limit, asks before overriding it. Null: the person said no. */
export async function submitConfirmingWip(
  taskId: string,
  confirm: DialogApi['confirm']
): Promise<Awaited<ReturnType<typeof tasksApi.submit>> | null> {
  try {
    return await tasksApi.submit(taskId)
  } catch (error) {
    if (!(error instanceof ApiError) || error.code !== WIP_LIMIT_REACHED_CODE) throw error
    const details = apiErrorDetails<WipLimitReachedDetails>(error)
    const ok = await confirm({
      kind: 'warning',
      title: 'WIP limit reached',
      message: details
        ? `The ${humanize(details.streamKey)} stream already has ${details.inFlight} of ${details.wipLimit} tasks in flight. Start this one anyway?`
        : 'The stream is at its WIP limit. Start this one anyway?',
      confirmLabel: 'Start anyway',
    })
    return ok ? await tasksApi.submit(taskId, { overrideWip: true }) : null
  }
}

/** "Start run" / "Retry" for a task: WIP confirmation, then to the run (or the blocked reason). */
export function useStartTask() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { alert, confirm } = useDialogs()
  return useMutation({
    mutationFn: (taskId: string) => submitConfirmingWip(taskId, confirm),
    // `run` is null for a task the workflow engine picked up instead of
    // dispatching directly (its first step may be a human gate, or its run
    // is created asynchronously) — nothing to navigate to in that case.
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
      void queryClient.invalidateQueries({ queryKey: ['task'] })
      void queryClient.invalidateQueries({ queryKey: ['project'] })
      if (!result) return
      if (result.run) void navigate({ to: '/runs/$runId', params: { runId: result.run.id } })
      else if (result.workflow?.blockedReason)
        void alert({
          kind: 'error',
          title: 'The task is blocked',
          message: result.workflow.blockedReason,
        })
    },
    onError: (e) =>
      void alert({
        kind: 'error',
        title:
          e instanceof ApiError && e.status === 422
            ? 'This task can’t start yet'
            : 'Couldn’t start the run',
        message: startError(e),
      }),
  })
}


export function TaskDrawer(props: {
  taskId: string
  onClose: () => void
  initialTab?: TaskDrawerTab
}) {
  // A new task id (a link inside the drawer) starts from scratch.
  return <TaskDrawerContent key={props.taskId} {...props} />
}

function TaskDrawerContent({
  taskId,
  onClose,
  initialTab,
}: {
  taskId: string
  onClose: () => void
  initialTab?: TaskDrawerTab
}) {
  const queryClient = useQueryClient()
  const lookups = useLookups()
  const [tab, setTab] = useState<TaskDrawerTab>(initialTab ?? 'details')
  // A bare `?taskId=` link (the "Report:" line in Plane and PR text) opens
  // on the Report tab when the task turns out to have one.
  const [preferReport, setPreferReport] = useState(initialTab === undefined)
  const [editing, setEditing] = useState(false)

  const taskQuery = useQuery({
    queryKey: ['task', taskId],
    queryFn: () => tasksApi.get(taskId),
    // While something is happening to the task, keep it live; idle tasks don't poll.
    refetchInterval: (query) =>
      query.state.data && isLive(query.state.data) ? LIVE_REFRESH_MS : false,
  })
  const task = taskQuery.data ?? null

  const reportQuery = useTaskReport(task?.id, {
    refetchInterval: task && isLive(task) ? 15_000 : false,
  })
  const report = reportQuery.data?.report ?? null
  useEffect(() => {
    if (!preferReport || !reportQuery.isSuccess) return
    if (report) setTab('report')
    setPreferReport(false)
  }, [preferReport, reportQuery.isSuccess, report])

  // Needed (only while blocked) to tell a retryable state from a terminal one.
  const workflowsQuery = useQuery({
    queryKey: ['project', task?.projectId ?? '', 'workflows'],
    queryFn: () => workflowsApi.get(task!.projectId),
    enabled: task != null && task.status === TaskStatus.Blocked && task.workflowKey != null,
  })

  const startMutation = useStartTask()

  const updateMutation = useMutation({
    mutationFn: (input: TaskEditInput) =>
      tasksApi.update(taskId, {
        title: input.title,
        instructions: input.instruction,
        acceptanceCriteria: input.acceptanceCriteria,
        riskLevel: input.riskLevel,
        priority: input.priority,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['task'] })
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
      void queryClient.invalidateQueries({ queryKey: ['project'] })
      setEditing(false)
    },
  })
  const editRiskHint = useRiskMismatchHint(editing ? task : null)

  function closeEdit(): void {
    updateMutation.reset()
    setEditing(false)
  }

  const startable = task != null && isStartable(task, workflowsQuery.data?.workflows)

  return (
    <>
      <Drawer
        open
        onClose={onClose}
        width={tab === 'report' ? 'max-w-5xl' : undefined}
        title={
          task ? (
            <>
              {task.ticketKey ? (
                <span className="mr-2 font-mono text-base font-normal text-ink-3">
                  {task.ticketKey}
                </span>
              ) : null}
              {task.title}
            </>
          ) : (
            ''
          )
        }
        description={
          task
            ? `${lookups.projectName(task.projectId)}, ${SOURCE_LABEL[task.source] ?? humanize(task.source)}`
            : undefined
        }
        footer={
          task ? (
            <>
              {isEditable(task) ? (
                <button type="button" className="btn-secondary" onClick={() => setEditing(true)}>
                  Edit
                </button>
              ) : null}
              {startable ? (
                <button
                  type="button"
                  className="btn-primary"
                  data-autofocus
                  disabled={startMutation.isPending}
                  onClick={() => startMutation.mutate(task.id)}
                >
                  {task.status === TaskStatus.Blocked ? 'Retry' : 'Start run'}
                </button>
              ) : null}
            </>
          ) : undefined
        }
      >
        {taskQuery.isLoading ? (
          <LoadingRows />
        ) : taskQuery.isError || !task ? (
          <ErrorNote error={taskQuery.error ?? new Error('Task not found.')} what="the task" />
        ) : (
          <div className="space-y-5">
            <Tabs
              label="Task sections"
              value={tab}
              onChange={setTab}
              options={[
                { value: 'details', label: 'Details' },
                {
                  value: 'report',
                  label: 'Report',
                  count: report?.unseenSubmissionCount,
                },
              ]}
            />
            {tab === 'report' ? (
              <TaskReport taskId={task.id} live={isLive(task)} />
            ) : (
              <TaskDetail
                task={task}
                hasReport={report != null}
                onShowReport={() => setTab('report')}
                onRetry={() => startMutation.mutate(task.id)}
                retrying={startMutation.isPending}
              />
            )}
          </div>
        )}
      </Drawer>

      <Drawer
        open={editing && task != null}
        onClose={closeEdit}
        title="Edit task"
        description={task ? lookups.projectName(task.projectId) : undefined}
      >
        {task ? (
          <TaskEditForm
            task={task}
            riskHint={editRiskHint}
            isSubmitting={updateMutation.isPending}
            error={
              updateMutation.isError
                ? updateMutation.error instanceof Error
                  ? updateMutation.error.message
                  : 'Couldn’t save the task.'
                : null
            }
            onCancel={closeEdit}
            onSubmit={(input) => updateMutation.mutate(input)}
          />
        ) : null}
      </Drawer>
    </>
  )
}

/** Stream, lane, severity and "Move to stream…" of a task. */
function TaskPlacement({ task }: { task: Task }) {
  const queryClient = useQueryClient()
  const [moving, setMoving] = useState(false)
  const [target, setTarget] = useState('')
  const [reason, setReason] = useState('')

  const streamsQuery = useQuery({
    queryKey: ['project', task.projectId, 'streams'],
    queryFn: () => streamsApi.list(task.projectId),
  })
  const streams = streamsQuery.data?.items ?? []
  const stream = streams.find((s) => s.key === task.streamKey)
  const otherStreams = streams.filter((s) => s.key !== task.streamKey && !s.workflowMissing)

  function refresh(): void {
    void queryClient.invalidateQueries({ queryKey: ['task'] })
    void queryClient.invalidateQueries({ queryKey: ['tasks'] })
    void queryClient.invalidateQueries({ queryKey: ['project', task.projectId] })
  }

  const severityMutation = useMutation({
    mutationFn: (severity: Severity | null) => tasksApi.update(task.id, { severity }),
    onSuccess: refresh,
  })

  const moveMutation = useMutation({
    mutationFn: () =>
      tasksApi.moveStream(task.id, {
        workflowKey: streams.find((s) => s.key === target)?.workflowKey ?? target,
        reason: reason.trim() || null,
      }),
    onSuccess: () => {
      refresh()
      setMoving(false)
      setTarget('')
      setReason('')
    },
  })

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-base text-ink-2">
        <span>
          Stream:{' '}
          <span className="font-medium text-ink">
            {stream?.name ?? (task.streamKey ? humanize(task.streamKey) : 'none')}
          </span>
        </span>
        {task.queueLane ? (
          <span>
            Lane: <span className="font-medium text-ink">{QUEUE_LANE_LABEL[task.queueLane]}</span>
          </span>
        ) : null}
        <label className="flex items-center gap-2">
          Severity
          <select
            aria-label="Severity"
            className="field-input h-8 w-auto py-0"
            value={task.severity ?? ''}
            disabled={severityMutation.isPending}
            onChange={(event) =>
              severityMutation.mutate((event.target.value || null) as Severity | null)
            }
          >
            <option value="">None</option>
            {SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          {task.severity ? (
            <span className={`rounded-full px-2 text-sm font-medium ${SEVERITY_CLASS[task.severity]}`}>
              {task.severity}
            </span>
          ) : null}
        </label>
        {otherStreams.length > 0 && task.workflowKey ? (
          <button
            type="button"
            className="btn-secondary btn-sm ml-auto"
            onClick={() => setMoving((v) => !v)}
          >
            Move to stream…
          </button>
        ) : null}
      </div>
      {task.queueReason ? <Notice tone="signal">{task.queueReason}</Notice> : null}
      {severityMutation.isError ? (
        <Notice tone="error">
          {severityMutation.error instanceof Error
            ? severityMutation.error.message
            : 'Couldn’t set the severity.'}
        </Notice>
      ) : null}

      {moving ? (
        <form
          className="space-y-3 rounded-control border border-line-strong p-3"
          onSubmit={(event) => {
            event.preventDefault()
            if (target) moveMutation.mutate()
          }}
        >
          <Field
            label="Move to stream"
            htmlFor="move-to-stream"
            hint="The task continues in the other stream’s workflow. A running step is cancelled."
          >
            <select
              id="move-to-stream"
              className="field-input"
              value={target}
              onChange={(event) => setTarget(event.target.value)}
              required
            >
              <option value="" disabled>
                Choose a stream
              </option>
              {otherStreams.map((s: StreamDefinition) => (
                <option key={s.key} value={s.key}>
                  {s.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Reason (optional)" htmlFor="move-stream-reason">
            <input
              id="move-stream-reason"
              className="field-input"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </Field>
          {moveMutation.isError ? (
            <Notice tone="error">
              {moveMutation.error instanceof Error
                ? moveMutation.error.message
                : 'Couldn’t move the task.'}
            </Notice>
          ) : null}
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-ghost" onClick={() => setMoving(false)}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={moveMutation.isPending || !target}>
              {moveMutation.isPending ? 'Moving…' : 'Move'}
            </button>
          </div>
        </form>
      ) : null}
    </section>
  )
}

/**
 * All states of the task's workflow, in order, as a compact stepper: past
 * states with the run(s) that ran there (agent, status, duration, summary
 * snippet, link to the run — each round separately, for a feedback loop
 * that repeated a state), the current state highlighted (a human gate shows
 * "waiting for your review" with a link to the approval), and the rest
 * greyed out as upcoming. Shares its run data with the `workflow_gate`
 * approval's own context (`tasksApi.steps` / `buildTaskSteps`,
 * api-side) — the task page and the gate never disagree about what ran.
 * Renders nothing for a legacy (non-workflow) task.
 */
function TaskWorkflowProgress({ task }: { task: Task }) {
  const workspaceId = useWorkspaceId()
  const stepsQuery = useQuery({
    queryKey: ['task', task.id, 'steps'],
    queryFn: () => tasksApi.steps(task.id),
    enabled: task.workflowKey != null,
    refetchInterval: isLive(task) ? LIVE_REFRESH_MS : false,
  })
  const workflowsQuery = useQuery({
    queryKey: ['project', task.projectId, 'workflows'],
    queryFn: () => workflowsApi.get(task.projectId),
    enabled: task.workflowKey != null,
  })
  const gateQuery = useQuery({
    queryKey: ['approvals', workspaceId, 'task', task.id, 'pending'],
    queryFn: () =>
      approvalsApi.list({ workspaceId, taskId: task.id, status: 'pending' }),
    enabled: task.workflowKey != null && task.status === TaskStatus.AwaitingApproval,
  })

  if (task.workflowKey == null) return null
  const definition = workflowsQuery.data?.workflows.find(
    (w) => w.key === task.workflowKey
  )
  if (!definition) return null

  const steps = stepsQuery.data?.steps ?? []
  const roundsByState = new Map<string, typeof steps>()
  for (const step of steps) {
    if (!step.workflowState) continue
    const forState = roundsByState.get(step.workflowState) ?? []
    forState.push(step)
    roundsByState.set(step.workflowState, forState)
  }
  const currentIndex = definition.states.findIndex(
    (s) => s.name === task.workflowState
  )
  const pendingGate = gateQuery.data?.items[0]

  return (
    <section>
      <h3 className="mb-2 text-sm font-medium text-ink-3">
        Workflow progress
      </h3>
      <ol className="space-y-3 border-l border-line pl-4">
        {definition.states.map((state, index) => {
          const rounds = roundsByState.get(state.name) ?? []
          const isCurrent = index === currentIndex
          const isPast = currentIndex >= 0 && index < currentIndex
          return (
            <li key={state.name} className="relative">
              <span
                aria-hidden
                className={`absolute -left-[21px] top-1 h-2 w-2 rounded-full ${
                  isCurrent ? 'bg-signal' : isPast ? 'bg-moss' : 'bg-line'
                }`}
              />
              <p
                className={`text-base font-medium ${
                  isCurrent
                    ? 'text-ink'
                    : isPast
                      ? 'text-ink-2'
                      : 'text-ink-3'
                }`}
              >
                {humanize(state.name)}
                {isCurrent && state.kind === 'human' ? (
                  <span className="ml-2 text-sm font-normal text-signal">
                    Waiting for your review
                    {pendingGate ? (
                      <>
                        {' · '}
                        <Link
                          to="/approvals"
                          search={{ id: pendingGate.id }}
                          className="link"
                        >
                          Open approval
                        </Link>
                      </>
                    ) : null}
                  </span>
                ) : null}
              </p>
              {rounds.length ? (
                <ul className="mt-1.5 space-y-1.5">
                  {rounds.map((step, roundIndex) =>
                    step.kind === 'review' ? (
                      <li key={step.runId} className="text-sm">
                        <span className="text-ink-2">{step.reviewerName ?? 'Reviewer'}</span>{' '}
                        <span className={step.decision === 'approved' ? 'text-moss' : 'text-danger'}>
                          {step.decision === 'approved' ? 'Approved' : 'Sent back'}
                        </span>
                        {step.note ? <p className="text-ink-3">“{step.note}”</p> : null}
                      </li>
                    ) : (
                    <li key={step.runId} className="text-sm">
                      <span className="text-ink-2">
                        {rounds.length > 1
                          ? `Round ${step.round ?? roundIndex + 1}: `
                          : ''}
                        {step.agentName ?? step.roleName ?? 'Agent'}
                      </span>{' '}
                      <StatusBadge status={step.status} />
                      <span className="ml-1.5 text-ink-3">
                        <LiveDuration startedAt={step.startedAt} endedAt={step.endedAt} />
                        {' · '}
                        <Link
                          to="/runs/$runId"
                          params={{ runId: step.runId }}
                          className="link"
                        >
                          run
                        </Link>
                      </span>
                      <StepChain step={step} />
                      {step.summary ? (
                        <p
                          className={`text-ink-3 ${step.status === 'blocked' ? 'whitespace-pre-wrap break-words' : 'truncate'}`}
                        >
                          {step.summary}
                        </p>
                      ) : null}
                    </li>
                    ),
                  )}
                </ul>
              ) : null}
            </li>
          )
        })}
      </ol>
    </section>
  )
}

/**
 * A task's workflow: current state, its history of transitions (plan §0.1
 * `TaskTransition`, one row per `task_transitions`) and, since a transition
 * "mag alles, ook terug" (plan §2 WS2), a manual "Move to…" to any other
 * state of the workflow. Renders nothing for a legacy (non-workflow) task.
 */
function TaskWorkflowSection({
  task,
  onRetry,
  retrying,
}: {
  task: Task
  onRetry: () => void
  retrying: boolean
}) {
  const queryClient = useQueryClient()
  const [moving, setMoving] = useState(false)
  const [toState, setToState] = useState('')
  const [reason, setReason] = useState('')

  const transitionsQuery = useQuery({
    queryKey: ['task', task.id, 'transitions'],
    queryFn: () => tasksApi.transitions(task.id),
    enabled: task.workflowKey != null,
    refetchInterval: isLive(task) ? LIVE_REFRESH_MS : false,
  })
  // Also needed (not just while moving) to tell a retryable blocked state
  // (agent/human) from a terminal one the "Try again" button can't resume.
  const workflowsQuery = useQuery({
    queryKey: ['project', task.projectId, 'workflows'],
    queryFn: () => workflowsApi.get(task.projectId),
    enabled:
      task.workflowKey != null &&
      (moving ||
        task.status === TaskStatus.Blocked ||
        // A decision card needs the step's options to change its answer.
        (transitionsQuery.data ?? []).some((t) => t.cause === 'decision')),
  })

  const moveMutation = useMutation({
    mutationFn: () =>
      tasksApi.transition(task.id, {
        toState,
        reason: reason.trim() || undefined,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
      void queryClient.invalidateQueries({
        queryKey: ['task', task.id, 'transitions'],
      })
      setMoving(false)
      setToState('')
      setReason('')
    },
  })

  if (task.workflowKey == null) return null

  const definition = workflowsQuery.data?.workflows.find(
    (w) => w.key === task.workflowKey
  )
  const otherStates = (definition?.states ?? []).filter(
    (s) => s.name !== task.workflowState
  )
  const transitions = transitionsQuery.data ?? []
  const currentState = definition?.states.find(
    (s) => s.name === task.workflowState
  )
  const canRetry = task.status === TaskStatus.Blocked && currentState?.kind !== 'terminal'
  const riskHint = riskMismatchHint(transitions[transitions.length - 1]?.reason)
  const lastDecisionId = [...transitions].reverse().find((t) => t.cause === 'decision')?.id

  return (
    <section>
      {riskHint ? <Notice tone="error">{riskHint}</Notice> : null}
      <div className="mb-1.5 mt-3 flex items-center justify-between">
        <h3 className="text-sm font-medium text-ink-3">Workflow</h3>
        <div className="flex items-center gap-2">
          {canRetry ? (
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={retrying}
              onClick={onRetry}
            >
              {retrying ? 'Retrying…' : 'Try again'}
            </button>
          ) : null}
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={() => setMoving((v) => !v)}
          >
            Move to…
          </button>
        </div>
      </div>
      <p className="text-base text-ink">
        {humanize(task.workflowKey)}
        {task.workflowState
          ? `, currently ${humanize(task.workflowState)}`
          : ''}
      </p>

      {moving ? (
        <form
          className="mt-3 space-y-3 rounded-control border border-line-strong p-3"
          onSubmit={(event) => {
            event.preventDefault()
            if (toState) moveMutation.mutate()
          }}
        >
          <Field label="Move to" htmlFor="move-to-state">
            {workflowsQuery.isLoading ? (
              <p className="text-sm text-ink-3">Loading states…</p>
            ) : (
              <select
                id="move-to-state"
                className="field-input"
                value={toState}
                onChange={(event) => setToState(event.target.value)}
                required
              >
                <option value="" disabled>
                  Choose a state
                </option>
                {otherStates.map((s) => (
                  <option key={s.name} value={s.name}>
                    {humanize(s.name)}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Reason (optional)" htmlFor="move-reason">
            <input
              id="move-reason"
              className="field-input"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </Field>
          {moveMutation.isError ? (
            <Notice tone="error">
              {moveMutation.error instanceof Error
                ? moveMutation.error.message
                : 'Couldn’t move the task.'}
            </Notice>
          ) : null}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn-ghost"
              onClick={() => setMoving(false)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={moveMutation.isPending || !toState}
            >
              {moveMutation.isPending ? 'Moving…' : 'Move'}
            </button>
          </div>
        </form>
      ) : null}

      <ol className="mt-3 space-y-2 border-l border-line pl-4">
        {transitionsQuery.isLoading ? (
          <li className="text-sm text-ink-3">Loading history…</li>
        ) : transitions.length ? (
          transitions.map((t, index) => (
            <li key={t.id} className="relative text-sm">
              <span
                aria-hidden
                className={`absolute -left-[21px] top-1 h-2 w-2 rounded-full ${t.cause === 'rejected' ? 'bg-danger' : 'bg-moss'}`}
              />
              <p className="text-ink">
                {t.fromState ? `${humanize(t.fromState)} → ` : ''}
                {humanize(t.toState)}
                <span className={`ml-1.5 ${t.cause === 'rejected' ? 'text-danger' : 'text-ink-3'}`}>
                  {t.cause === 'approved' || t.cause === 'rejected'
                    ? `(${t.cause === 'approved' ? 'Approved' : 'Sent back'}${t.actorName ? ` by ${t.actorName}` : ''})`
                    : `(${humanize(t.cause)}${t.actorName ? ` by ${t.actorName}` : ''})`}
                </span>
              </p>
              {t.cause === 'decision' ? (
                <DecisionCard
                  task={task}
                  transition={t}
                  decisionState={findDecisionState(
                    workflowsQuery.data?.workflows,
                    t.fromState,
                    transitions[index - 1]?.workflowKey ?? t.workflowKey
                  )}
                  canChange={t.id === lastDecisionId}
                />
              ) : null}
              {t.reason && (t.cause === 'approved' || t.cause === 'rejected') ? (
                <p className="whitespace-pre-wrap text-ink-2">“{t.reason}”</p>
              ) : null}
              <p className="text-ink-3">
                {formatRelative(t.createdAt)}
                {t.runId ? (
                  <>
                    {' · '}
                    <Link
                      to="/runs/$runId"
                      params={{ runId: t.runId }}
                      className="link"
                    >
                      run
                    </Link>
                  </>
                ) : null}
                {t.reason && t.cause !== 'approved' && t.cause !== 'rejected' && t.cause !== 'decision' ? ` · ${t.reason}` : ''}
              </p>
            </li>
          ))
        ) : (
          <li className="text-sm text-ink-3">No transitions yet.</li>
        )}
      </ol>
    </section>
  )
}

/** Why the task is blocked, in full, right under the status badges: the transition's reason plus what the blocking run itself reported. */
function BlockedBanner({ task }: { task: Task }) {
  const blocked = task.status === TaskStatus.Blocked && task.workflowKey != null
  const reason = useBlockReason(task)
  const transitionsQuery = useQuery({
    queryKey: ['task', task.id, 'transitions'],
    queryFn: () => tasksApi.transitions(task.id),
    enabled: blocked,
  })
  const stepsQuery = useQuery({
    queryKey: ['task', task.id, 'steps'],
    queryFn: () => tasksApi.steps(task.id),
    enabled: blocked,
  })
  if (task.status !== TaskStatus.Blocked) return null
  const transitions = transitionsQuery.data ?? []
  const runId = transitions[transitions.length - 1]?.runId
  const steps = (stepsQuery.data?.steps ?? []).filter((s) => s.kind !== 'review')
  const step = steps.find((s) => s.runId === runId) ?? steps[steps.length - 1]
  const blockers = step?.blockers ?? []
  return (
    <Notice tone="error">
      <p className="font-medium">This task is blocked</p>
      {step?.summary ? (
        <p className="mt-1 whitespace-pre-wrap break-words text-ink-2">{step.summary}</p>
      ) : null}
      {blockers.length ? (
        <ul className="mt-1 list-disc space-y-0.5 pl-5 text-ink-2">
          {blockers.map((b) => (
            <li key={b} className="whitespace-pre-wrap break-words">{b}</li>
          ))}
        </ul>
      ) : null}
      {reason || !step?.summary ? (
        <p className="mt-1 whitespace-pre-wrap break-words text-sm text-ink-3">
          {reason ?? 'No reason was recorded. See the workflow history below.'}
        </p>
      ) : null}
      {step ? (
        <Link to="/runs/$runId" params={{ runId: step.runId }} className="link mt-1 inline-block text-sm">
          Open the run
        </Link>
      ) : null}
    </Notice>
  )
}

const ARCHIVABLE_STATUSES: readonly string[] = [TaskStatus.Completed, TaskStatus.Cancelled, TaskStatus.Rejected]

/** Archive a finished task (it leaves the board and the default list), or restore an archived one. */
function TaskArchiveSection({ task }: { task: Task }) {
  const queryClient = useQueryClient()
  const archived = task.archivedAt != null
  const mutation = useMutation({
    mutationFn: () => (archived ? tasksApi.unarchive(task.id) : tasksApi.archive(task.id)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['task'] })
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
      void queryClient.invalidateQueries({ queryKey: ['project'] })
    },
  })
  if (!archived && !ARCHIVABLE_STATUSES.includes(task.status)) return null
  return (
    <section className="flex flex-wrap items-center gap-3 border-t border-line pt-4">
      <button type="button" className="btn-secondary btn-sm" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
        {archived ? 'Restore from archive' : 'Archive'}
      </button>
      <span className="text-sm text-ink-3">
        {archived ? 'Archived: hidden from the board and the task list.' : 'Hides this finished task from the board and the task list.'}
      </span>
      {mutation.isError ? (
        <Notice tone="error">
          {mutation.error instanceof Error ? mutation.error.message : 'Couldn’t update the archive.'}
        </Notice>
      ) : null}
    </section>
  )
}

function TaskDetail({
  task,
  hasReport,
  onShowReport,
  onRetry,
  retrying,
}: {
  task: Task
  hasReport: boolean
  onShowReport: () => void
  onRetry: () => void
  retrying: boolean
}) {
  const refs = task.externalReferences ?? []
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <StatusBadge status={task.status} />
        <StatusBadge status={task.riskLevel} />
        <ModelTierBadge task={task} />
        <span className="text-base text-ink-2">Priority {task.priority}</span>
        {hasReport ? (
          <button
            type="button"
            className="link ml-auto text-sm"
            onClick={onShowReport}
          >
            View report
          </button>
        ) : null}
        {task.pullRequestUrl ? (
          <a
            href={task.pullRequestUrl}
            target="_blank"
            rel="noreferrer"
            className={`link flex items-center gap-1.5 text-sm ${hasReport ? '' : 'ml-auto'}`}
          >
            <ClayIcon name="pull-request" size={18} />
            Pull request
          </a>
        ) : null}
      </div>
      <BlockedBanner task={task} />
      <SubtaskParentLine task={task} />
      <TaskPlacement task={task} />
      <TaskWorkflowProgress task={task} />
      <TaskWorkflowSection task={task} onRetry={onRetry} retrying={retrying} />
      <WorkflowVariablesPanel task={task} />
      <SubtasksPanel task={task} />
      <section>
        <h3 className="mb-1.5 text-sm font-medium text-ink-3">Instruction</h3>
        <p className="whitespace-pre-wrap text-base text-ink">
          {task.instruction || 'No instruction given.'}
        </p>
      </section>
      {task.acceptanceCriteria?.length ? (
        <section>
          <h3 className="mb-1.5 text-sm font-medium text-ink-3">Done when</h3>
          <ClayList icon="checkbox" items={task.acceptanceCriteria} />
        </section>
      ) : null}
      {task.maxBudgetCents != null ? (
        <section>
          <h3 className="mb-1.5 text-sm font-medium text-ink-3">Budget</h3>
          <p className="text-base text-ink">
            {formatMoney(task.maxBudgetCents)}
          </p>
        </section>
      ) : null}
      <TaskLinks task={task} />
      <CustomVarsSection task={task} />
      <TaskArchiveSection task={task} />
      {refs.length ? (
        <section>
          <h3 className="mb-1.5 text-sm font-medium text-ink-3">External references</h3>
          <ul className="space-y-1">
            {refs.map((ref) => (
              <li key={`${ref.system}-${ref.externalId}`} className="text-base">
                {ref.url ? (
                  <a
                    href={ref.url}
                    target="_blank"
                    rel="noreferrer"
                    className="link"
                  >
                    {refLabel(ref)}
                  </a>
                ) : (
                  <span className="text-ink">
                    {refLabel(ref)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  )
}

export function TaskCreateDrawer({
  project,
  workflowKey,
  onClose,
  onCreated,
}: {
  project: Project
  /** Preselected stream (its workflow key). */
  workflowKey?: string | null
  onClose: () => void
  onCreated: (task: Task) => void
}) {
  const workspaceId = useWorkspaceId()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const lookups = useLookups()
  const { alert, confirm } = useDialogs()

  const streamsQuery = useQuery({
    queryKey: ['project', project.id, 'streams'],
    queryFn: () => streamsApi.list(project.id),
  })
  const streams = (streamsQuery.data?.items ?? []).filter((s) => !s.workflowMissing)

  const createMutation = useMutation({
    mutationFn: async ({ input, start }: { input: TaskCreateInput; start: boolean }) => {
      const task = await tasksApi.createManual({
        workspaceId,
        projectId: project.id,
        title: input.title,
        instructions: input.instruction,
        riskLevel: input.riskLevel,
        acceptanceCriteria: input.acceptanceCriteria,
        workflowKey: input.workflowKey,
        desiredRoleId: input.desiredRoleId,
        severity: input.severity,
        ...(input.customVars ? { customVars: input.customVars } : {}),
      })
      let submitted: Awaited<ReturnType<typeof tasksApi.submit>> | null = null
      if (start) {
        try {
          submitted = await submitConfirmingWip(task.id, confirm)
        } catch (error) {
          // The task exists; only starting it failed.
          void alert({ kind: 'error', title: 'The task was created but didn’t start', message: startError(error) })
        }
      }
      return {
        task: submitted?.task ?? task,
        run: submitted?.run ?? null,
        workflow: submitted?.workflow ?? null,
      }
    },
    onSuccess: ({ task, run, workflow }) => {
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
      void queryClient.invalidateQueries({ queryKey: ['project', project.id] })
      onCreated(task)
      if (run) void navigate({ to: '/runs/$runId', params: { runId: run.id } })
      else if (workflow?.blockedReason)
        void alert({ kind: 'error', title: 'The task is blocked', message: workflow.blockedReason })
    },
  })

  const selectedStream = streams.find((s) => s.workflowKey === workflowKey)
  const isBug = selectedStream?.order === 'severity_age'

  return (
    <Drawer
      open
      onClose={onClose}
      title={isBug ? 'Report bug' : 'New task'}
      description={`${project.name}. Goblic picks the agent from the project’s workflow.`}
    >
      {streamsQuery.isLoading ? (
        <LoadingRows />
      ) : (
        <TaskCreateForm
          project={project}
          roles={lookups.roles}
          streams={streams}
          initialWorkflowKey={workflowKey ?? null}
          isSubmitting={createMutation.isPending}
          error={createMutation.isError ? startError(createMutation.error) : null}
          onCancel={onClose}
          onSubmit={(input, start) => createMutation.mutate({ input, start })}
        />
      )}
    </Drawer>
  )
}

interface TaskCreateInput {
  projectId: string
  title: string
  instruction: string
  riskLevel: RiskLevel
  acceptanceCriteria: string[]
  /** Null: let the submit route resolve the project's default workflow. */
  workflowKey: string | null
  /** Only meaningful for a project with no workflows configured (legacy dispatch). */
  desiredRoleId: string | null
  /** Only offered for a severity-ordered (bug) stream. */
  severity: Severity | null
  /** Optional tags for external systems; left out when none were entered. */
  customVars?: CustomVars
}

const RISK_HINT: Record<RiskLevel, string> = {
  low: 'Docs, tests, small fixes.',
  medium: 'Normal feature work.',
  high: 'Touches auth, data or infrastructure.',
  critical: 'Could cause an outage or data loss.',
}

/** A project's Definition of Done, stripped of leading bullet/number markers — same rule the server falls back on (tasks.service.ts's `resolveAcceptanceCriteria`). */
function definitionOfDoneLines(project: Project | undefined): string[] {
  return (project?.definitionOfDone ?? []).map((line) =>
    line.replace(/^[-*•]\s+/, '').replace(/^\d+[.)]\s+/, '').trim()
  )
}

function TaskCreateForm({
  project,
  roles,
  streams,
  initialWorkflowKey,
  isSubmitting,
  error,
  onCancel,
  onSubmit,
}: {
  project: Project
  roles: Role[]
  streams: StreamDefinition[]
  initialWorkflowKey: string | null
  isSubmitting: boolean
  error: ReactNode | null
  onCancel: () => void
  onSubmit: (input: TaskCreateInput, start: boolean) => void
}) {
  const workflows = project.policy?.workflows ?? []
  const hasWorkflows = workflows.length > 0 || streams.length > 0
  const [form, setForm] = useState<TaskCreateInput>({
    projectId: project.id,
    title: '',
    instruction: '',
    riskLevel: RiskLevel.Medium,
    acceptanceCriteria: [''],
    workflowKey: initialWorkflowKey,
    desiredRoleId: null,
    severity: null,
  })
  // Tasks land in their stream's queue; starting right away skips it. Without
  // workflows (legacy direct dispatch) starting is the only way to get going.
  const [startAfter, setStartAfter] = useState(!hasWorkflows)
  // Optional key/value tags for external systems (same editor as the task drawer's Custom vars).
  const [varRows, setVarRows] = useState<VarRow[]>([])
  const [varsError, setVarsError] = useState<string | null>(null)

  const effectiveWorkflowKey =
    form.workflowKey ?? project.policy?.defaultWorkflow ?? streams[0]?.workflowKey ?? ''
  const selectedStream = streams.find((s) => s.workflowKey === effectiveWorkflowKey)
  const askSeverity = selectedStream?.order === 'severity_age'
  const doneLines = definitionOfDoneLines(project)

  function setCriterionLine(index: number, value: string): void {
    const next = [...form.acceptanceCriteria]
    next[index] = value
    setForm({ ...form, acceptanceCriteria: next })
  }

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    // TODO: client-side validation mirroring createManualTaskSchema
    // (packages/api/src/services/tasks.service.ts) before submit.
    const vars = formRowsToVars(varRows)
    if ('error' in vars) {
      setVarsError(vars.error)
      return
    }
    setVarsError(null)
    onSubmit(
      {
        ...form,
        acceptanceCriteria: form.acceptanceCriteria
          .map((line) => line.trim())
          .filter((line) => line.length > 0),
        workflowKey: hasWorkflows ? effectiveWorkflowKey || null : null,
        severity: askSeverity ? form.severity : null,
        ...(Object.keys(vars.vars).length > 0 ? { customVars: vars.vars } : {}),
      },
      startAfter
    )
  }

  return (
    <form id="task-create" className="space-y-5" onSubmit={handleSubmit}>
      <Field label="Title" htmlFor="task-title">
        <input
          id="task-title"
          className="field-input"
          placeholder="Add rate limiting to the import endpoint"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          required
        />
      </Field>

      <Field
        label="Instruction"
        htmlFor="task-instruction"
        hint="What should change, and how will you know it’s done?"
      >
        <textarea
          id="task-instruction"
          className="field-input"
          rows={7}
          value={form.instruction}
          onChange={(e) => setForm({ ...form, instruction: e.target.value })}
          required
        />
      </Field>

      <Field
        label="Done when"
        htmlFor="task-criterion-0"
        hint="One line per criterion. Left empty, the project’s Definition of Done is used."
      >
        <div className="space-y-2">
          {form.acceptanceCriteria.map((line, index) => (
            <div key={index} className="flex gap-2">
              <input
                id={index === 0 ? 'task-criterion-0' : undefined}
                className="field-input"
                placeholder={
                  doneLines[index] ??
                  (index === 0 ? 'Meets the instructions and all tests pass.' : '')
                }
                value={line}
                onChange={(e) => setCriterionLine(index, e.target.value)}
              />
              <button
                type="button"
                className="btn-ghost btn-sm"
                aria-label="Remove criterion"
                onClick={() =>
                  setForm({
                    ...form,
                    acceptanceCriteria:
                      form.acceptanceCriteria.length > 1
                        ? form.acceptanceCriteria.filter((_, i) => i !== index)
                        : [''],
                  })
                }
              >
                ×
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          className="btn-secondary btn-sm mt-2"
          onClick={() =>
            setForm({
              ...form,
              acceptanceCriteria: [...form.acceptanceCriteria, ''],
            })
          }
        >
          Add criterion
        </button>
      </Field>

      {hasWorkflows ? (
        <>
          <Field
            label="Stream"
            htmlFor="task-workflow"
            hint="Which flow this task moves through, from the queue to done."
          >
            <select
              id="task-workflow"
              className="field-input"
              value={effectiveWorkflowKey}
              onChange={(e) => setForm({ ...form, workflowKey: e.target.value })}
              required
            >
              <option value="" disabled>
                Choose a stream
              </option>
              {streams.length > 0
                ? streams.map((s) => (
                    <option key={s.key} value={s.workflowKey}>
                      {s.name}
                    </option>
                  ))
                : workflows.map((w) => (
                    <option key={w.key} value={w.key}>
                      {w.name}
                    </option>
                  ))}
            </select>
          </Field>
          {askSeverity ? (
            <Field label="Severity" htmlFor="task-severity" hint="S1 skips the queue when the stream allows it.">
              <select
                id="task-severity"
                className="field-input"
                value={form.severity ?? ''}
                onChange={(e) =>
                  setForm({ ...form, severity: (e.target.value || null) as Severity | null })
                }
              >
                <option value="">Not set yet</option>
                {SEVERITIES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
          ) : null}
        </>
      ) : (
        <Field
          label="Preferred role (optional)"
          htmlFor="task-role"
          hint="This project has no workflows configured — leave it to the policy, or pin a role."
        >
          <select
            id="task-role"
            className="field-input"
            value={form.desiredRoleId ?? ''}
            onChange={(e) =>
              setForm({ ...form, desiredRoleId: e.target.value || null })
            }
          >
            <option value="">Any (from policy)</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </Field>
      )}

      <fieldset>
        <legend className="mb-1.5 text-base font-medium text-ink">Risk</legend>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {Object.values(RiskLevel).map((level) => {
            const checked = form.riskLevel === level
            return (
              <label
                key={level}
                className={`cursor-pointer rounded-control border px-3 py-2 text-center text-base font-medium transition-colors ${
                  checked
                    ? 'border-moss bg-moss-soft text-moss-strong'
                    : 'border-line-strong text-ink-2 hover:bg-sunken'
                }`}
              >
                <input
                  type="radio"
                  name="risk"
                  value={level}
                  checked={checked}
                  onChange={() => setForm({ ...form, riskLevel: level })}
                  className="sr-only"
                />
                {humanize(level)}
              </label>
            )
          })}
        </div>
        <p className="mt-1.5 text-sm text-ink-3">
          {RISK_HINT[form.riskLevel]} Higher risk means more approvals along the
          way.
        </p>
      </fieldset>

      <Field
        label="Custom vars (optional)"
        htmlFor="task-custom-vars"
        hint="Tags for external systems, e.g. source_system = jira. Goblic only stores them so the task can be found again."
      >
        <div id="task-custom-vars">
          <CustomVarsEditor rows={varRows} onChange={setVarRows} emptyText="" />
        </div>
        {varsError ? <p className="mt-2 text-sm text-danger">{varsError}</p> : null}
      </Field>

      <label className="flex items-center gap-2.5 text-base text-ink">
        <input
          type="checkbox"
          checked={startAfter}
          onChange={(e) => setStartAfter(e.target.checked)}
          className="h-4 w-4 accent-[rgb(var(--moss))]"
        />
        Start a run right away{hasWorkflows ? ' (skips the queue)' : ''}
      </label>

      {error ? <Notice tone="error">{error}</Notice> : null}

      <div className="flex justify-end gap-2 border-t border-line pt-4">
        <button type="button" className="btn-ghost" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={isSubmitting}>
          {isSubmitting
            ? 'Creating…'
            : startAfter
              ? 'Create and start'
              : 'Create task'}
        </button>
      </div>
    </form>
  )
}
interface TaskEditInput {
  title: string
  instruction: string
  acceptanceCriteria: string[]
  riskLevel: RiskLevel
  priority: number
}

/**
 * Edits the fields a task's creator can revise after the fact (plan
 * "runners": a manually added workflow task whose risk level a role's
 * ceiling rejected couldn't be fixed before — PATCH /tasks/:id now allows
 * it whenever the task has no active run). Project/role/workflow choices
 * are fixed at creation and not offered here.
 */
function TaskEditForm({
  task,
  riskHint,
  isSubmitting,
  error,
  onCancel,
  onSubmit,
}: {
  task: Task
  riskHint: string | null
  isSubmitting: boolean
  error: string | null
  onCancel: () => void
  onSubmit: (input: TaskEditInput) => void
}) {
  const [form, setForm] = useState<TaskEditInput>({
    title: task.title,
    instruction: task.instruction,
    acceptanceCriteria: task.acceptanceCriteria.length > 0 ? task.acceptanceCriteria : [''],
    riskLevel: task.riskLevel,
    priority: task.priority,
  })

  function setCriterionLine(index: number, value: string): void {
    const next = [...form.acceptanceCriteria]
    next[index] = value
    setForm({ ...form, acceptanceCriteria: next })
  }

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    onSubmit({
      ...form,
      acceptanceCriteria: form.acceptanceCriteria
        .map((line) => line.trim())
        .filter((line) => line.length > 0),
    })
  }

  return (
    <form id="task-edit" className="space-y-5" onSubmit={handleSubmit}>
      {riskHint ? <Notice tone="error">{riskHint}</Notice> : null}

      <Field label="Title" htmlFor="task-edit-title">
        <input
          id="task-edit-title"
          className="field-input"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          required
        />
      </Field>

      <Field label="Instruction" htmlFor="task-edit-instruction">
        <textarea
          id="task-edit-instruction"
          className="field-input"
          rows={7}
          value={form.instruction}
          onChange={(e) => setForm({ ...form, instruction: e.target.value })}
          required
        />
      </Field>

      <Field
        label="Done when"
        htmlFor="task-edit-criterion-0"
        hint="One line per criterion."
      >
        <div className="space-y-2">
          {form.acceptanceCriteria.map((line, index) => (
            <div key={index} className="flex gap-2">
              <input
                id={index === 0 ? 'task-edit-criterion-0' : undefined}
                className="field-input"
                value={line}
                onChange={(e) => setCriterionLine(index, e.target.value)}
              />
              <button
                type="button"
                className="btn-ghost btn-sm"
                aria-label="Remove criterion"
                onClick={() =>
                  setForm({
                    ...form,
                    acceptanceCriteria:
                      form.acceptanceCriteria.length > 1
                        ? form.acceptanceCriteria.filter((_, i) => i !== index)
                        : [''],
                  })
                }
              >
                ×
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          className="btn-secondary btn-sm mt-2"
          onClick={() =>
            setForm({
              ...form,
              acceptanceCriteria: [...form.acceptanceCriteria, ''],
            })
          }
        >
          Add criterion
        </button>
      </Field>

      <fieldset>
        <legend className="mb-1.5 text-base font-medium text-ink">Risk</legend>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {Object.values(RiskLevel).map((level) => {
            const checked = form.riskLevel === level
            return (
              <label
                key={level}
                className={`cursor-pointer rounded-control border px-3 py-2 text-center text-base font-medium transition-colors ${
                  checked
                    ? 'border-moss bg-moss-soft text-moss-strong'
                    : 'border-line-strong text-ink-2 hover:bg-sunken'
                }`}
              >
                <input
                  type="radio"
                  name="edit-risk"
                  value={level}
                  checked={checked}
                  onChange={() => setForm({ ...form, riskLevel: level })}
                  className="sr-only"
                />
                {humanize(level)}
              </label>
            )
          })}
        </div>
        <p className="mt-1.5 text-sm text-ink-3">
          {RISK_HINT[form.riskLevel]} Higher risk means more approvals along the
          way.
        </p>
      </fieldset>

      <Field label="Priority" htmlFor="task-edit-priority" hint="0 (low) to 10 (high).">
        <input
          id="task-edit-priority"
          type="number"
          min={0}
          max={10}
          className="field-input"
          value={form.priority}
          onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })}
        />
      </Field>

      {error ? <Notice tone="error">{error}</Notice> : null}

      <div className="flex justify-end gap-2 border-t border-line pt-4">
        <button type="button" className="btn-ghost" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={isSubmitting}>
          {isSubmitting ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  )
}

